import {
  base_colorAlert,
  base_colorContentBorder,
  base_colorRaisedSurface,
  text_colorTextPrimary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { font_4xl, font_family_weight_medium } from 'ext-ui/style/typography'
import { css } from 'styled-components'

/*
 * Shared chrome for the Bill Pay flyouts (AddVendorFlyout — M2, PayBillFlyout —
 * M3). These blocks were duplicated verbatim in both style files, kept in sync
 * by a comment promising they'd stay identical; this module makes that
 * structural instead of aspirational.
 *
 * Two things are encoded here:
 *
 * 1. The pinned header/footer workaround. BaseFlyout has no footer or stepper
 *    slot (a DS gap — the same workaround appears in AssignAccountOwnerFlyout,
 *    FiltersFlyout, RightFlyout and TransactionDetail). Making Content + Body
 *    flex columns and clipping their overflow is what keeps the header and
 *    footer fixed while only the field list scrolls. The real fix is a `footer`
 *    slot on BaseFlyout itself — filed, not done here.
 *
 * 2. The WHITE surface. BaseFlyout defaults its body to base_colorSurface (pale
 *    grey), the older flyout look. Per Richa (2026-08-05) the Card Transactions
 *    flyout is the pattern to standardize on: white throughout, with dividers
 *    and visual grouping instead of grey content cards. Mirrors
 *    TransactionDetail.style.tsx.
 */

export const paddingHorizontal = spacing(10)
export const footerPaddingHorizontal = spacing(5)

/**
 * Root surface override. Takes the flyout's own root class (e.g.
 * `'PayBillFlyout'`) because BaseFlyout puts the css-prop class and that class
 * on the same portal element.
 *
 * The Body/CloseBar rules are nested inside .BaseFlyout-Content deliberately:
 * BaseFlyout declares its own rules at that depth, so a flat ".BaseFlyout-Body"
 * selector loses the specificity contest and the surface silently stays grey.
 * .BaseFlyout-Content carries a background of its own too, so recolouring only
 * the Body is not enough.
 */
export const flyoutSurface = (rootClass: string) => css`
  &.${rootClass} {
    .BaseFlyout-Content {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      background-color: ${base_colorRaisedSurface};

      .BaseFlyout-CloseBar {
        background-color: ${base_colorRaisedSurface};
      }

      .BaseFlyout-Body {
        padding: 0;
        flex: auto;
        min-height: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        background-color: ${base_colorRaisedSurface};
      }
    }
  }
`

/** Title block with an inset bottom divider. No stepper / progress indicator —
    an explicit design guardrail on both Bill Pay flyouts. */
export const header = css`
  position: relative;
  padding: ${spacing(6)} ${paddingHorizontal};
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: ${paddingHorizontal};
    right: ${paddingHorizontal};
    height: 1px;
    background-color: ${base_colorContentBorder};
  }
`

/** Modifiers for `header`, composed as `css={[header, <modifier>]}`.

    The rule (EX-40842): these flyout headers are sticky, so content scrolls
    under them and they need exactly one boundary line.
    - No tabs → keep the divider, run it edge to edge: `headerDividerFullBleed`.
    - Tabs → drop it: `headerNoDivider`. The tab bar's own full-width underline
      is the boundary; both together read as a doubled line.

    Full-bleed marks a structural boundary; inset rules mean content grouping. */
export const headerDividerFullBleed = css`
  &::after {
    left: 0;
    right: 0;
  }
`

export const headerNoDivider = css`
  &::after {
    content: none;
  }
`

export const flyoutTitle = css`
  margin: 0;
  font-family: ${font_family_weight_medium};
  /* Stop the browser layering a synthetic bold over Rubik-Medium's own weight
     — the prototype's flyout titles are Title Case medium, not bold. */
  font-weight: 500;
  ${font_4xl}
  color: ${text_colorTextPrimary};
`

/** Pinned footer. Divider, not a change of surface — same white as the body,
    separated by the border alone. */
export const footer = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};
  padding: ${spacing(6)} ${footerPaddingHorizontal};
  border-top: 1px solid ${base_colorContentBorder};
  background: ${base_colorRaisedSurface};
`

/**
 * The footer action row: primary anchored right, secondary — when there is one
 * — pushed left. Stated that way round deliberately. `space-between` encodes
 * "spread the children," which silently means "left-align" the moment a footer
 * renders only one button, and these footers do: every call site makes its
 * secondary conditional, and Pay Bill's review step drops "Go back" for three
 * of its six modes. Anchoring to the end instead keeps the primary in one
 * place whatever the child count, so the CTA can't move under a cursor
 * mid-submit.
 *
 * The secondary is the first child at every call site.
 */
export const footerButtons = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  > *:first-child:not(:only-child) {
    margin-right: auto;
  }
`

/**
 * Screen-reader-only. Both Bill Pay flyouts report an in-flight save with the
 * primary button's own spinner and no visible status banner (a status
 * AlertPanel appears nowhere else in the app). The announcement still has to
 * exist: submitting disables the CTA and the acknowledgement, which drops
 * keyboard focus and would otherwise leave a screen-reader user with no signal
 * at all (WCAG 2.1 AA 4.1.3).
 */
export const visuallyHidden = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

/** Required-field marker. Margin rather than a whitespace text node: some
    labels render as flex containers, which drop whitespace-only text nodes, so
    a `{' '}` between the label and the asterisk silently vanishes there while
    working in a non-flex label. Matches the gap TextInput's own .label-field
    uses. */
export const requiredAsterisk = css`
  color: ${base_colorAlert};
  margin-left: ${spacing(1)};
`
